import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/lib/workflow";
import {
  Users, LogOut, Settings as SettingsIcon, Contact, FileText, Inbox, Wrench,
  LayoutDashboard, TrendingUp, ShoppingCart, IndianRupee, Palette, Ruler, Warehouse,
  Menu, X,
} from "lucide-react";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard,testid: "nav-dashboard", roles: ["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "admin", "design", "production_design", "dealer"] },
  { to: "/enquiries", label: "Enquiries", icon: Inbox,        testid: "nav-enquiries", roles: ["super_admin", "sales", "sales_coordination", "production_coordination", "dealer"] },
  { to: "/quotes",    label: "Quotes",    icon: FileText,     testid: "nav-quotes",    roles: ["sales", "sales_coordination", "production_coordination", "accounts", "super_admin", "dealer"] },
  { to: "/sales",     label: "Sales",     icon: IndianRupee,  testid: "nav-sales",     roles: ["super_admin", "sales", "sales_coordination", "accounts", "admin"], permission: "access_sales_receivables" },
  { to: "/projects",  label: "Projects",  icon: Wrench,       testid: "nav-projects",  roles: ["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "admin", "production_design", "dealer"] },
  { to: "/customers", label: "Customers", icon: Contact,      testid: "nav-customers", roles: ["sales", "sales_coordination", "production_coordination", "super_admin"] },
  { to: "/costing",   label: "Costing",   icon: TrendingUp,   testid: "nav-costing",   roles: ["super_admin", "accounts"], permission: "access_costing" },
  { to: "/costing/purchase-board", label: "Purchase Board", icon: ShoppingCart, testid: "nav-purchase-board", roles: ["super_admin", "accounts", "admin", "production_coordination"], permission: "access_costing" },
  { to: "/store",     label: "Store",     icon: Warehouse,    testid: "nav-store",     roles: ["super_admin", "store", "accounts", "admin"] },
  { to: "/production-design/work", label: "Production Drawings", icon: Ruler, testid: "nav-prod-design-work", roles: ["super_admin", "production_design"] },
  { to: "/design/work", label: "2D / 3D Drawings", icon: Palette, testid: "nav-design-work", roles: ["super_admin", "design"] },
  { to: "/users",     label: "Users",     icon: Users,        testid: "nav-users",     roles: ["super_admin"] },
  { to: "/settings",  label: "Settings",  icon: SettingsIcon, testid: "nav-settings",  roles: ["super_admin", "dealer"] },
  { to: "/settings/item-ids", label: "Item IDs", icon: SettingsIcon, testid: "nav-itemids", roles: ["super_admin", "production_design"] },
];

export default function DesktopLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const roleMeta = ROLES[user?.role] || {};
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever we route to a new page.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const visibleNav = NAV.filter((n) => {
    const hasRole = !n.roles || n.roles.includes(user?.role) || user?.role === "super_admin";
    const hasPerm = n.permission && !!(user?.permissions || {})[n.permission];
    return hasRole || hasPerm;
  });

  const renderNav = (extraClass = "") => (
    <nav className={`flex-1 py-6 px-3 space-y-1 ${extraClass}`}>
      {visibleNav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={item.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-sm transition-colors text-sm font-semibold ${
              isActive ? "text-white" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`
          }
          style={({ isActive }) => isActive ? { backgroundColor: "#E52B50" } : undefined}
        >
          <item.icon className="w-4 h-4" strokeWidth={2.25} />
          {item.label}
        </NavLink>
      ))}
      <div className="pt-3 mt-3 border-t border-slate-200">
        <NotificationBell />
      </div>
    </nav>
  );

  const renderFooter = () => (
    <div className="px-3 py-4 border-t border-slate-200">
      <div className="px-4 pb-3">
        <div className="hk-tiny-label text-slate-500">Signed in as</div>
        <div className="font-semibold text-sm text-slate-900">{user?.name}</div>
        <div className="text-xs text-slate-500">{roleMeta.label}</div>
      </div>
      <button
        type="button"
        data-testid="logout-button"
        onClick={async () => { await logout(); navigate("/login"); }}
        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
      >
        <LogOut className="w-4 h-4" /> Logout
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      {/* Desktop sidebar — fixed. */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-white text-slate-800 border-r border-slate-200" data-testid="desktop-sidebar">
        <div className="px-6 py-7 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Logo className="h-14 w-14 shrink-0" />
            <div>
              <div className="hk-tiny-label leading-none" style={{ color: "#E52B50" }}>Hygiene</div>
              <div className="font-black text-lg leading-tight text-slate-900" style={{ fontFamily: "Chivo" }}>KITCHENS</div>
            </div>
          </div>
        </div>
        {renderNav()}
        {renderFooter()}
      </aside>

      {/* Mobile drawer — off-canvas, overlays the content when open. */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-scrim"
          />
          <aside className="relative w-72 max-w-[85vw] bg-white flex flex-col shadow-2xl">
            <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Logo className="h-10 w-10 shrink-0" />
                <div>
                  <div className="hk-tiny-label leading-none" style={{ color: "#E52B50" }}>Hygiene</div>
                  <div className="font-black text-base leading-tight text-slate-900" style={{ fontFamily: "Chivo" }}>KITCHENS</div>
                </div>
              </div>
              <button type="button" onClick={() => setMobileOpen(false)}
                className="p-2 rounded-sm hover:bg-slate-100"
                data-testid="mobile-drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">{renderNav("py-4")}</div>
            {renderFooter()}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="md:hidden bg-white border-b border-slate-200 text-slate-900 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-sm hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Logo className="h-9 w-9 shrink-0" />
            <span className="font-black text-sm" style={{ fontFamily: "Chivo" }}>HYGIENE KITCHENS</span>
          </div>
          <button data-testid="mobile-logout" onClick={async () => { await logout(); navigate("/login"); }} className="text-slate-600 p-2">
            <LogOut className="w-4 h-4" />
          </button>
        </header>
        <main className="p-3 sm:p-6 lg:p-10 max-w-[1400px] mx-auto overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
